'use client'

/*
 * VisualMemoryTest
 *
 * Tiles flash on a grid, you click them back. The grid grows, three lives.
 *
 * WHAT THE PREVIOUS TWO TOOLS TAUGHT, APPLIED HERE FROM THE START:
 *
 *  - No tier renders grey. The reaction test originally gave its Average band a
 *    slate gradient, so the outcome most people get turned the payoff screen
 *    grey and the whole thing read as a shrug. Every tier below is a real
 *    colour, and the slower ones go warmer rather than duller.
 *  - The result keeps the colour of the game rather than dropping to a white
 *    card, because falling from a vivid board to a plain panel kills the energy
 *    exactly where it should peak.
 *  - The CTA sits directly under the number. On digit span it started fifth on
 *    the screen behind a curve, a tease strip and two charts, and no eye ever
 *    reached it.
 *  - The score counts up instead of appearing.
 *  - The prompt mounts only on the result, so its timer starts when the score
 *    lands and it can never interrupt a round.
 *
 * ABOUT THE COMPARISON. A score with nothing to compare it against is dead on
 * arrival, so this does give one, and it is careful about what it claims.
 *
 * There is no normative sample for this format. Tile counts, grid growth, flash
 * timing and mistake tolerance vary between implementations, so a level here is
 * not comparable with a level elsewhere and nobody has standardised it. What is
 * consistently reported is the shape: most people finish somewhere between
 * level 5 and 10, and getting past 12 is uncommon. LEVEL_MEAN and LEVEL_SD below
 * describe that shape, and the figure shown is explicitly an estimate from it.
 *
 * The distinction matters and the UI keeps it. Digit span says "percentile",
 * because published WAIS norms sit behind it. This says "better than about", and
 * a footnote states the basis. Inventing a distribution is fine as long as you
 * never call it a measurement.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { Grid3x3, RotateCcw, ArrowRight, Heart } from 'lucide-react'
import { normalCDF } from '@/lib/iqStats'
import TestPromptPopup from '@/components/TestPromptPopup'

const LIVES = 3
const FLASH_BASE = 900
const FLASH_PER_TILE = 120

// Grid widens every three levels and the pattern gains a tile each level, so
// difficulty climbs on two axes at once. One axis alone plateaus: more tiles on
// a 3x3 becomes trivial pattern-fill, and a bigger grid with few tiles gets
// easier, not harder.
const gridSizeFor = (level) => Math.min(6, 3 + Math.floor((level - 1) / 3))
const tileCountFor = (level) => {
  const g = gridSizeFor(level)
  return Math.min(level + 2, g * g - 2)
}

// The shape of typical performance on this format, not a normative sample.
// See the note at the top of the file for why these are an estimate and are
// labelled as one everywhere they surface.
const LEVEL_MEAN = 7.5
const LEVEL_SD = 2.5

// Share of people expected to reach this level or beyond.
const topPercentFor = (level) => (1 - normalCDF((level - LEVEL_MEAN) / LEVEL_SD)) * 100

const TIERS = [
  { min: 14, label: 'Photographic', sub: 'Genuinely exceptional', ring: 'from-fuchsia-500 to-purple-600' },
  { min: 11, label: 'Sharp', sub: 'Well beyond the usual', ring: 'from-cyan-500 to-blue-600' },
  { min: 8, label: 'Strong', sub: 'Comfortably above typical', ring: 'from-emerald-500 to-teal-600' },
  { min: 5, label: 'Solid', sub: 'Around where most people finish', ring: 'from-indigo-500 to-violet-600' },
  { min: 3, label: 'Warming up', sub: 'Worth another go', ring: 'from-amber-500 to-orange-600' },
  { min: 0, label: 'Just started', sub: 'It climbs quickly, try again', ring: 'from-rose-500 to-pink-600' },
]
const tierFor = (level) => TIERS.find((t) => level >= t.min)

function pickTiles(count, cells) {
  const set = new Set()
  while (set.size < count) set.add(Math.floor(Math.random() * cells))
  return set
}

export default function VisualMemoryTest() {
  const [phase, setPhase] = useState('idle') // idle | flashing | input | levelUp | finished
  const [level, setLevel] = useState(1)
  const [lives, setLives] = useState(LIVES)
  const [pattern, setPattern] = useState(new Set())
  const [found, setFound] = useState(new Set())
  const [wrong, setWrong] = useState(new Set())
  const [best, setBest] = useState(null)
  const [isNewBest, setIsNewBest] = useState(false)
  const [countUp, setCountUp] = useState(0)

  const timers = useRef([])
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  useEffect(() => clearTimers, [])

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('biqt_visual_memory_best')
      setBest(stored ? parseInt(stored, 10) : null)
    } catch { setBest(null) }
  }, [])

  const gridSize = gridSizeFor(level)
  const cells = gridSize * gridSize

  const startLevel = useCallback((lv) => {
    clearTimers()
    const g = gridSizeFor(lv)
    const p = pickTiles(tileCountFor(lv), g * g)
    setPattern(p)
    setFound(new Set())
    setWrong(new Set())
    setPhase('flashing')
    timers.current.push(
      setTimeout(() => setPhase('input'), FLASH_BASE + p.size * FLASH_PER_TILE),
    )
  }, [])

  const start = () => {
    setLevel(1)
    setLives(LIVES)
    setIsNewBest(false)
    startLevel(1)
  }

  const finish = useCallback((reached) => {
    setPhase('finished')
    if (best === null || reached > best) {
      setIsNewBest(best !== null)
      setBest(reached)
      try { window.localStorage.setItem('biqt_visual_memory_best', String(reached)) } catch {}
    }
  }, [best])

  const clickTile = (i) => {
    if (phase !== 'input' || found.has(i) || wrong.has(i)) return

    if (pattern.has(i)) {
      const nextFound = new Set(found).add(i)
      setFound(nextFound)
      if (nextFound.size === pattern.size) {
        setPhase('levelUp')
        timers.current.push(setTimeout(() => {
          setLevel((l) => { startLevel(l + 1); return l + 1 })
        }, 650))
      }
      return
    }

    const nextWrong = new Set(wrong).add(i)
    setWrong(nextWrong)
    const livesLeft = lives - 1
    setLives(livesLeft)

    if (livesLeft <= 0) {
      timers.current.push(setTimeout(() => finish(level), 700))
    } else {
      // Reveal the full pattern briefly, then replay the same level. Replaying
      // rather than advancing is what makes a mistake cost something.
      timers.current.push(setTimeout(() => startLevel(level), 900))
    }
  }

  useEffect(() => {
    if (phase !== 'finished') { setCountUp(0); return }
    let frame
    const started = performance.now()
    const tick = (now) => {
      const t = Math.min(1, (now - started) / 650)
      setCountUp(Math.round((1 - Math.pow(1 - t, 3)) * level))
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [phase, level])

  const tier = tierFor(level)
  const topPercent = topPercentFor(level)
  const beatPercent = Math.max(1, Math.min(99, Math.round(100 - topPercent)))

  const tileClass = (i) => {
    if (phase === 'flashing' && pattern.has(i)) return 'bg-white shadow-lg scale-[0.97]'
    if (wrong.has(i)) return 'bg-rose-400'
    if (found.has(i)) return 'bg-white shadow-md'
    return 'bg-white/15 hover:bg-white/25'
  }

  return (
    <div className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden">
      {phase !== 'finished' ? (
        <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 px-6 sm:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs font-semibold text-blue-200 uppercase tracking-wide">Level</p>
              <p className="text-3xl font-bold text-white tabular-nums leading-none">{level}</p>
            </div>
            <p className="text-sm font-semibold text-white/90 min-h-5">
              {phase === 'idle' && 'Ready when you are'}
              {phase === 'flashing' && 'Memorise the pattern'}
              {phase === 'input' && `Click the ${pattern.size} tiles`}
              {phase === 'levelUp' && 'Correct'}
            </p>
            <div className="flex gap-1">
              {Array.from({ length: LIVES }).map((_, i) => (
                <Heart
                  key={i}
                  className={`w-5 h-5 transition-all ${i < lives ? 'text-rose-400 fill-rose-400' : 'text-white/25'}`}
                />
              ))}
            </div>
          </div>

          <div
            className="grid gap-2 mx-auto w-full max-w-sm aspect-square"
            style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: cells }).map((_, i) => (
              <button
                key={i}
                type="button"
                disabled={phase !== 'input'}
                onClick={() => clickTile(i)}
                aria-label={`Tile ${i + 1}`}
                className={`rounded-xl transition-all duration-150 ${tileClass(i)} ${phase === 'input' ? 'cursor-pointer' : 'cursor-default'}`}
              />
            ))}
          </div>

          {phase === 'idle' && (
            <div className="text-center mt-7">
              <button
                type="button"
                onClick={start}
                className="inline-flex items-center bg-white text-blue-700 hover:bg-blue-50 font-bold text-lg px-10 py-4 rounded-2xl shadow-lg transition-all"
              >
                <Grid3x3 className="w-5 h-5 mr-2" />
                Start
              </button>
              <p className="text-blue-100 text-sm mt-4 max-w-xs mx-auto leading-relaxed">
                Tiles flash, then you click them back. The grid grows each level.
                Three lives.
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className={`bg-gradient-to-br ${tier.ring} px-6 sm:px-8 py-10 text-center`}>
            {isNewBest && (
              <span className="inline-block px-3 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-xs font-bold tracking-wide mb-4">
                NEW PERSONAL BEST
              </span>
            )}
            <p className="text-xs font-semibold text-white/70 uppercase tracking-[0.2em] mb-2">
              Level reached
            </p>
            <p className="text-8xl sm:text-9xl font-bold text-white tabular-nums leading-none drop-shadow-sm">
              {countUp}
            </p>
            <p className="text-3xl sm:text-4xl font-bold text-white mt-4 tracking-tight">{tier.label}</p>
            <p className="text-white/80 mt-1">{tier.sub}</p>
            <div className="flex items-center justify-center gap-2 flex-wrap mt-5">
              {topPercent <= 25 && (
                <span className="inline-block px-4 py-1.5 rounded-full bg-white text-gray-900 text-sm font-bold tabular-nums shadow-sm">
                  TOP {Math.max(1, Math.round(topPercent))}%
                </span>
              )}
              <span className="inline-block px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-semibold tabular-nums">
                Better than about {beatPercent}% of people
              </span>
            </div>
            <p className="text-white/70 text-xs mt-3">
              {tileCountFor(level)} tiles on a {gridSizeFor(level)} by {gridSizeFor(level)} grid
            </p>
          </div>

          <div className="p-6 sm:p-8">
            <Link
              href="/test"
              className="group relative block w-full overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-[length:200%_100%] hover:bg-[position:100%_0] p-[3px] shadow-xl shadow-purple-500/25 transition-[background-position] duration-700"
            >
              <span className="block rounded-[13px] bg-white px-6 py-5 text-center transition-colors group-hover:bg-transparent">
                <span className="block text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent group-hover:text-white transition-colors">
                  Now find your actual IQ
                </span>
                <span className="mt-1 flex items-center justify-center text-sm font-medium text-gray-500 group-hover:text-blue-100 transition-colors">
                  50 questions · all four domains · instant score
                  <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                </span>
              </span>
            </Link>

            <p className="text-sm text-gray-600 text-center leading-relaxed mt-5">
              This is visuospatial working memory. Holding a pattern is not the
              same as reasoning about one, and reasoning is most of what a score
              is made of.
            </p>

            <div className="flex items-center justify-center gap-5 mt-5">
              <button
                type="button"
                onClick={start}
                className="inline-flex items-center text-gray-600 hover:text-gray-900 font-semibold text-sm transition-colors"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Play again
              </button>
              {best !== null && (
                <span className="text-sm text-gray-400 tabular-nums">Best: level {best}</span>
              )}
            </div>

            <p className="text-xs text-gray-400 mt-5 text-center leading-relaxed">
              That comparison is an estimate from how people typically perform on
              this format, not a normed percentile, because no standardised
              sample exists for grid memory tasks. Where this site quotes a true
              percentile, such as on the digit span test, published normative
              data sits behind it.
            </p>
          </div>
        </>
      )}

      {phase === 'finished' && (
        <TestPromptPopup
          delayMs={4000}
          storageKey="biqt_visual_memory_prompt"
          headline="Good recall. Now the reasoning."
          body={
            <>
              You just measured visuospatial working memory. Our 50-question
              assessment covers verbal, numerical and spatial reasoning too, and
              gives you a full-scale score with your exact percentile.
            </>
          }
        />
      )}
    </div>
  )
}
